<template>
    <canvas ref="canvasRef" :style="{ width: width + 'px', height: height + 'px' }"></canvas>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps({
    text: {
        type: String,
        default: ''
    },
    baseFontSize: {
        type: Number,
        default: 12 // 基准字体大小，会乘以缩放比例
    },
    color: {
        type: String,
        default: '#ffffff'
    },
    fontWeight: {
        type: [String, Number],
        default: 600
    },
    fontFamily: {
        type: String,
        default: 'system-ui, -apple-system, sans-serif'
    }
})

const canvasRef = ref<HTMLCanvasElement | null>(null)
const width = ref(0)
const height = ref(0)

// 获取缩放比例
const getScaleRatio = (): number => {
    const ratio = getComputedStyle(document.documentElement).getPropertyValue('--scale-ratio')
    return parseFloat(ratio) || 1
}

const render = () => {
    const canvas = canvasRef.value
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const scaleRatio = getScaleRatio()
    const actualFontSize = props.baseFontSize * scaleRatio
    const font = `${props.fontWeight} ${actualFontSize * dpr}px ${props.fontFamily}`

    // 测量文字宽度
    ctx.font = font
    const metrics = ctx.measureText(props.text)
    const textWidth = Math.ceil(metrics.width)
    const textHeight = Math.ceil(actualFontSize * dpr * 1.2)

    // 设置 canvas 实际尺寸
    canvas.width = textWidth
    canvas.height = textHeight

    // 设置显示尺寸
    width.value = textWidth / dpr
    height.value = textHeight / dpr

    // 清空画布（透明背景）
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // 绘制文字
    ctx.font = font
    ctx.fillStyle = props.color
    ctx.textBaseline = 'middle'
    ctx.fillText(props.text, 0, textHeight / 2)
}

// 监听窗口大小变化，重新渲染
const handleResize = () => {
    nextTick(render)
}

onMounted(() => {
    nextTick(render)
    window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
})

watch(() => [props.text, props.baseFontSize, props.color, props.fontWeight], () => {
    nextTick(render)
}, { deep: true })
</script>

<style scoped>
canvas {
    display: inline-block;
    vertical-align: middle;
}
</style>
